import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    alpha,
    useTheme,
    Grid
} from '@mui/material';
import {
    Search as SearchIcon,
    FileDownload as ReportIcon,
    Email as EmailIcon,
    ShoppingCart as OrdersIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import api from '../../utils/api';

const SalesHistory = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const { showNotification } = useNotification();

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });

    // Filters
    const [search, setSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [status, setStatus] = useState('');

    // Report dialog
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [reportStartDate, setReportStartDate] = useState('');
    const [reportEndDate, setReportEndDate] = useState('');
    const [reportLoading, setReportLoading] = useState(false);

    const fetchSales = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (search) params.append('search', search);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (paymentMethod) params.append('paymentMethod', paymentMethod);
            if (status) params.append('status', status);

            const response = await api.get(`/sales?${params.toString()}`);
            setSales(response.data.sales);
            setPagination(response.data.pagination);
        } catch (err) {
            showNotification('Failed to load sales data', 'error');
        } finally {
            setLoading(false);
        }
    }, [search, startDate, endDate, paymentMethod, status, showNotification]);

    useEffect(() => {
        fetchSales(1);
    }, [fetchSales]);

    const handlePageChange = (event, page) => {
        fetchSales(page);
    };

    const handleClearFilters = () => {
        setSearch('');
        setStartDate('');
        setEndDate('');
        setPaymentMethod('');
        setStatus('');
    };

    const handleGenerateReport = async () => {
        if (!reportStartDate || !reportEndDate) {
            showNotification('Please select both start and end dates', 'warning');
            return;
        }
        setReportLoading(true);
        try {
            const response = await api.post('/sales/report', {
                startDate: reportStartDate,
                endDate: reportEndDate
            });
            showNotification(response.data.message, 'success');
            setReportDialogOpen(false);
            setReportStartDate('');
            setReportEndDate('');
        } catch (err) {
            showNotification(err.response?.data?.message || 'Failed to generate report', 'error');
        } finally {
            setReportLoading(false);
        }
    };

    const getPaymentChipColor = (method) => {
        switch (method) {
            case 'cash': return 'success';
            case 'card': return 'primary';
            case 'digital': return 'info';
            default: return 'default';
        }
    };

    const hasActiveFilters = search || startDate || endDate || paymentMethod || status;

    return (
        <Box>
            {/* Filters */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FilterIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="subtitle2" color="text.secondary">Filters</Typography>
                    {hasActiveFilters && (
                        <Button size="small" onClick={handleClearFilters} sx={{ ml: 'auto', textTransform: 'none' }}>
                            Clear All
                        </Button>
                    )}
                </Box>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by sale # or customer..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="From"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="To"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Payment</InputLabel>
                            <Select
                                value={paymentMethod}
                                label="Payment"
                                onChange={(e) => setPaymentMethod(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="card">Card</MenuItem>
                                <MenuItem value="digital">Digital</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                label="Status"
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="voided">Voided</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Sales Table */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    overflow: 'hidden',
                }}
            >
                {/* Summary bar */}
                <Box sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        {pagination.total} {pagination.total === 1 ? 'transaction' : 'transactions'} found
                    </Typography>
                    {user?.role === 'admin' && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReportIcon />}
                            onClick={() => setReportDialogOpen(true)}
                            sx={{ textTransform: 'none' }}
                        >
                            Generate Report
                        </Button>
                    )}
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Transaction</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Customer</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Branch</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Cashier</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Payment</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>Date & Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }} align="right">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center', borderBottom: 'none' }}>
                                        <CircularProgress size={40} />
                                    </TableCell>
                                </TableRow>
                            ) : sales.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center', borderBottom: 'none' }}>
                                        <OrdersIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            No sales found
                                        </Typography>
                                        <Typography variant="body2" color="text.disabled">
                                            {hasActiveFilters ? 'Try adjusting your filters' : 'Sales will appear here once transactions are made'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sales.map((sale) => (
                                    <TableRow key={sale.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                {sale.saleNumber}
                                            </Typography>
                                            <Typography variant="caption" color="text.disabled">
                                                {sale.itemsCount} {sale.itemsCount === 1 ? 'item' : 'items'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{sale.customer || 'Walk-in Customer'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={sale.branch}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                    color: 'primary.main',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    height: 24
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{sale.cashier}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={sale.paymentMethod}
                                                size="small"
                                                color={getPaymentChipColor(sale.paymentMethod)}
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 22, textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {new Date(sale.time).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: sale.status === 'voided' ? 'error.main' : 'success.main' }}>
                                                KES {sale.amount?.toLocaleString() || '0'}
                                            </Typography>
                                            {sale.status === 'voided' && (
                                                <Chip label="Voided" size="small" color="error" sx={{ fontSize: '0.65rem', height: 18, mt: 0.5 }} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Pagination
                            count={pagination.totalPages}
                            page={pagination.page}
                            onChange={handlePageChange}
                            color="primary"
                            size="medium"
                        />
                    </Box>
                )}
            </Paper>

            {/* Generate Report Dialog */}
            <Dialog
                open={reportDialogOpen}
                onClose={() => !reportLoading && setReportDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 48, height: 48, borderRadius: 2,
                            bgcolor: 'primary.main',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <ReportIcon sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Generate Sales Report
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Report will be emailed to {user?.email}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Start Date"
                            value={reportStartDate}
                            onChange={(e) => setReportStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label="End Date"
                            value={reportEndDate}
                            onChange={(e) => setReportEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading} sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerateReport}
                        disabled={reportLoading || !reportStartDate || !reportEndDate}
                        startIcon={reportLoading ? <CircularProgress size={20} /> : <EmailIcon />}
                    >
                        {reportLoading ? 'Sending...' : 'Send Report'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SalesHistory;
